import { AxiosResponse } from "axios";
import api from "./api";
import { logger } from "../utils/logger";
import i18n from "../i18n";

export interface IUser {
  _id: string;
  firstName: string;
  lastName: string;
  phone: string;
  country_code: string;
  email?: string;
  userType: "Owner" | "User" | "Administrator" | "Washer";
  gender?: "Male" | "Female" | "Other";
  dateOfBirth?: string;
  isBlocked: boolean;
  lastSeenAt?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface IUsersResponse {
  status: string;
  message: string;
  data: {
    results: number;
    users: IUser[];
  };
}

interface IUserResponse {
  status: string;
  data: IUser;
}

class UserService {
  /**
   * Get all users
   */
  public async getUsers(): Promise<IUser[]> {
    try {
      logger.info("Fetching all users");
      const response: AxiosResponse<IUsersResponse> = await api.get("/admins/users");
      
      logger.info(`Successfully fetched ${response.data.data.results} users`);
      return response.data.data.users;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchUsers"));
    }
  }

  /**
   * Get user by ID
   */
  public async getUserById(userId: string): Promise<IUser> {
    try {
      logger.info(`Fetching user with ID: ${userId}`);
      const response: AxiosResponse<IUserResponse> = await api.get(`/admins/users/${userId}`);

      logger.info("Successfully fetched user");
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchUser"));
    }
  }

  /**
   * Update user fields (admin)
   */
  public async updateUser(
    userId: string,
    fields: Partial<Pick<IUser, "firstName" | "lastName" | "email" | "gender" | "dateOfBirth">>
  ): Promise<IUser> {
    try {
      logger.info(`Updating user: ${userId}`);
      const response: AxiosResponse<IUserResponse> = await api.patch(`/admins/users/${userId}`, fields);
      logger.info("Successfully updated user");
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.updateUser"));
    }
  }


  /**
   * Delete user
   */
  public async deleteUser(userId: string): Promise<void> {
    try {
      logger.info(`Deleting user: ${userId}`);
      await api.delete(`/admins/users/${userId}`);

      logger.info("Successfully deleted user");
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.deleteUser"));
    }
  }

  /**
   * Block a user (admin action — prevents OTP login)
   */
  public async blockUser(userId: string): Promise<IUser> {
    try {
      logger.info(`Blocking user: ${userId}`);
      const response: AxiosResponse<IUserResponse> = await api.patch(`/admins/users/${userId}/block`);
      logger.info("Successfully blocked user");
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.blockUser"));
    }
  }

  /**
   * Unblock a user (admin action)
   */
  public async unblockUser(userId: string): Promise<IUser> {
    try {
      logger.info(`Unblocking user: ${userId}`);
      const response: AxiosResponse<IUserResponse> = await api.patch(`/admins/users/${userId}/unblock`);
      logger.info("Successfully unblocked user");
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.unblockUser"));
    }
  }

  /**
   * Get user's washing history
   */
  public async getUserWashingHistory(userId: string): Promise<any[]> {
    try {
      logger.info(`Fetching washing history for user: ${userId}`);
      const response: AxiosResponse = await api.get(`/users/${userId}/washing-history`);
      
      logger.info("Successfully fetched user washing history");
      return response.data.data || [];
    } catch (error: any) {
      logger.warn("Failed to fetch user washing history, returning empty array");
      return [];
    }
  }

  /**
   * Handle API errors
   */
  private handleError(error: any, defaultMessage: string): never {
    const errorMessage =
      error.response?.data?.message || error.message || defaultMessage;
    logger.error(errorMessage);
    throw new Error(errorMessage);
  }
}

const userServiceInstance = new UserService();
export default userServiceInstance;
